import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import StoreX, { store } from '../../redux/oldStore';
import { ServerResponse } from '../../utils/Server';
import Modal from '../Modal/Modal';
import Nominations from '../Nominations/Nominations';
import { FormatDate } from '../../utils/Tools';
import { IJudgePortalAssignment, IJudgePortalData } from './JudgePortalTypes';
import './JudgePortal.css';

const formatTime = (time?: string) => FormatDate(time, 'time') ?? '';

const formatState = (state?: string | null) => state || 'Not Started';

const supportedCategory = (name: string) => {
  const n = (name || '').toLowerCase();
  return (
    n.includes('website') ||
    n.includes('paper') ||
    n.includes('exhibit') ||
    n.includes('performance') ||
    n.includes('podcast') ||
    n.includes('poster') ||
    n.includes('documentary')
  );
};

const fileUrl = (key: string) => StoreX.BuildUrl(`../File/Image/${key}`);
const paperUrl = (key: string) => StoreX.BuildUrl(`../File/showFile/${key}`);
const websiteUrl = (code: string) => `https://${code}.NHDWebCentral.org`;

const entryHref = (
  a: IJudgePortalAssignment,
  mode: 'self' | 'for' | 'review',
  judgeId?: string
) => {
  const base = StoreX.BuildUrlWithFair(
    `../app/judge-portal/entry?id=${a.projectKey}&evalId=${a.evaluationId}&scheduleAssignmentId=${a.scheduleRoundAssignmentId}&judgeGroupAssignmentId=${a.judgeGroupAssignmentId}&finals=${a.isFinalRound}`
  );
  if (mode === 'for' && judgeId) return `${base}&mode=for&judgeId=${judgeId}`;
  if (mode === 'review' && judgeId) return `${base}&mode=review&judgeId=${judgeId}`;
  return base;
};

const JudgePortalPage = () => {
  const [data, setData] = useState<IJudgePortalData | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('');
  const [rankingOpen, setRankingOpen] = useState<number | null>(null);
  const [ranks, setRanks] = useState<Record<number, string>>({});
  const [nomProjectId, setNomProjectId] = useState<string | null>(null);

  const judgeGroupId = StoreX.GetQueryStringValue('judgeGroupId');

  const load = useCallback(() => {
    setLoading(true);
    const payload: any = {};
    if (judgeGroupId) payload.judgeGroupId = Number(judgeGroupId);
    store.server
      .postApi<ServerResponse<IJudgePortalData>>('../JudgePortal/PortalData', payload)
      .then((x) => {
        if (x.Success && x.Value) {
          setData(x.Value);
        } else {
          toast.error(x.Message || 'Unable to load judge portal.', { autoClose: false });
          setData(null);
        }
      })
      .catch(() => {
        toast.error('Unable to load judge portal.', { autoClose: false });
        setData(null);
      })
      .finally(() => setLoading(false));
  }, [judgeGroupId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.data === 'child-done-refresh-me') load();
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [load]);

  const groups = useMemo(() => {
    if (!data?.assignments?.length) return [] as { key: string; rows: IJudgePortalAssignment[] }[];
    const map = new Map<string, IJudgePortalAssignment[]>();
    data.assignments.forEach((a) => {
      const key = `${a.judgeGroupId}-${a.roundId}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(a);
    });
    return Array.from(map.entries())
      .map(([key, rows]) => ({
        key,
        rows: rows.sort((a, b) => (a.judgeNumber ?? 0) - (b.judgeNumber ?? 0)),
      }))
      .sort((a, b) => (a.rows[0]?.judgeGroupName || '').localeCompare(b.rows[0]?.judgeGroupName || ''));
  }, [data]);

  useEffect(() => {
    if (!activeTab && groups.length) setActiveTab(groups[0].key);
  }, [groups, activeTab]);

  const judgeGroups = useMemo(() => {
    if (!data?.assignments?.length) return [] as IJudgePortalAssignment[][];
    const map = new Map<number, IJudgePortalAssignment[]>();
    data.assignments.forEach((a) => {
      if (!map.has(a.judgeGroupId)) map.set(a.judgeGroupId, []);
      map.get(a.judgeGroupId)!.push(a);
    });
    return Array.from(map.values()).sort((a, b) =>
      (a[0]?.judgeGroupName || '').localeCompare(b[0]?.judgeGroupName || '')
    );
  }, [data]);

  const openRanking = (groupRows: IJudgePortalAssignment[]) => {
    const assignmentId = groupRows[0].judgeGroupAssignmentId;
    const projects = uniqueProjects(groupRows);
    const next: Record<number, string> = {};
    projects.forEach((p) => {
      next[p.projectId] = p.roundRank != null && p.roundRank > 0 ? String(p.roundRank) : '';
    });
    setRanks(next);
    setRankingOpen(assignmentId);
  };

  const submitRanking = (groupRows: IJudgePortalAssignment[]) => {
    const group = groupRows[0];
    const projects = uniqueProjects(groupRows);
    const rankValues: string[] = [];
    const rankPayload: { EntryId: number; Value: number; RoundId: number }[] = [];

    for (const p of projects) {
      const rank = ranks[p.projectId] ?? '';
      if (rank.length === 0) {
        toast.warning('All entries must have a rank!');
        return;
      }
      if (Number(rank) < 0) {
        toast.warning('All ranks must be a positive number!');
        return;
      }
      if (rankValues.includes(rank) && rank !== '99') {
        toast.warning('Two entries may not have the same ranking!');
        return;
      }
      rankValues.push(rank);
      rankPayload.push({ EntryId: p.projectId, Value: Number(rank), RoundId: group.roundId });
    }

    if (!window.confirm('Are you sure you want to submit rankings?')) return;

    store.server
      .postApi<ServerResponse<boolean>>('../JudgePortal/SubmitRanking', {
        JudgeGroupId: group.judgeGroupId,
        JudgeGroupAssignmentId: group.judgeGroupAssignmentId,
        Ranks: rankPayload,
      })
      .then((x) => {
        if (x.Success) {
          toast.success('Rankings submitted.');
          setRankingOpen(null);
          load();
        } else {
          toast.error(x.Message || 'Could not submit rankings.', { autoClose: false });
        }
      });
  };

  const submitAndNotify = (assignmentId: number) => {
    if (!window.confirm('Are you sure you want to submit to NHD? This will lock the room.')) return;
    store.server
      .postApi<ServerResponse<boolean>>('../JudgePortal/SubmitAndNotify', {
        judgeGroupAssignmentId: assignmentId,
      })
      .then((x) => {
        if (x.Success) {
          toast.success('Room locked and NHD notified.');
          load();
        } else {
          toast.error(x.Message || 'Could not lock room.', { autoClose: false });
        }
      });
  };

  if (loading) {
    return (
      <div className="main-body-page grow judge-portal-page" data-testid="judge-portal-page">
        <p className="text-muted">Loading judge portal…</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="main-body-page grow judge-portal-page" data-testid="judge-portal-page">
        <div className="alert alert-warning">Judge portal is not available.</div>
      </div>
    );
  }

  const virtualRooms = data.onlineJudgingVirtualInterviews || data.onlineJudgingVirtualJudgingRooms;
  const activeGroup = groups.find((g) => g.key === activeTab);

  return (
    <div className="main-body-page grow judge-portal-page" data-testid="judge-portal-page">
      <div className="row">
        <div className={virtualRooms ? 'col-sm-6' : 'col-sm-12'}>
          <h3>Judge Groups</h3>
          {judgeGroups.length === 0 && (
            <div className="alert alert-info">No judge group assignments found.</div>
          )}
          {judgeGroups.map((jg) => {
            const group = jg[0];
            const judges = uniqueJudges(jg);
            return (
              <div key={group.judgeGroupId} className="panel panel-default">
                <div className="panel-heading">
                  <div className="row">
                    <div className="col-sm-8">
                      <strong>{group.judgeGroupName}</strong>
                    </div>
                    <div className="col-sm-4 text-right">
                      {virtualRooms && (
                        <a
                          className="btn btn-default"
                          href={StoreX.BuildUrlWithFair(
                            `../app/video/${encodeURIComponent(
                              `${data.fairId}-${group.judgeGroupId}`.replace(/[^A-z0-9/-]/g, '')
                            )}`
                          )}
                          target="_blank"
                          rel="noreferrer"
                          title="Open judges video room"
                        >
                          <i className="fad fa-users-class" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
                <div className="panel-body">
                  <table className="table table-hover table-striped">
                    <thead>
                      <tr>
                        <th>Judges</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {judges.map((j, i) => (
                        <tr key={j.personId}>
                          <td>
                            {i + 1} - {j.firstName} {j.lastName}
                          </td>
                          <td className="text-right">
                            <div>{j.email}</div>
                            <div>{j.cellPhone}</div>
                            <div>{j.phone}</div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <h3>Judging Assignments</h3>
      <div className="btn-group" style={{ marginBottom: '1rem', flexWrap: 'wrap' }}>
        {groups.map((g) => (
          <button
            key={g.key}
            type="button"
            className={`btn btn-default tab-btn ${activeTab === g.key ? 'active btn-primary' : ''}`}
            onClick={() => setActiveTab(g.key)}
          >
            {g.rows[0].judgeGroupName}
          </button>
        ))}
      </div>

      {activeGroup && (
        <AssignmentTab
          data={data}
          rows={activeGroup.rows}
          onOpenRanking={() => openRanking(activeGroup.rows)}
          onLock={() => submitAndNotify(activeGroup.rows[0].judgeGroupAssignmentId)}
          onNominate={(projectKey) => setNomProjectId(projectKey)}
        />
      )}

      {rankingOpen != null && activeGroup && (
        <Modal
          setModalOpen={(open: boolean) => {
            if (!open) setRankingOpen(null);
          }}
          title="Submit Entry Ranking"
        >
          <div>
            {uniqueProjects(activeGroup.rows).map((p) => (
              <div className="form-group row" key={p.projectId}>
                <label className="control-label col-sm-6" htmlFor={`rank-${p.projectId}`}>
                  {p.projectDisplayId}
                </label>
                <div className="col-sm-3">
                  <input
                    id={`rank-${p.projectId}`}
                    type="number"
                    className="form-control"
                    value={ranks[p.projectId] ?? ''}
                    onChange={(e) =>
                      setRanks((prev) => ({ ...prev, [p.projectId]: e.target.value }))
                    }
                  />
                </div>
              </div>
            ))}
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => submitRanking(activeGroup.rows)}
            >
              <i className="fa fa-cubes" /> Submit Ranking
            </button>
          </div>
        </Modal>
      )}

      {nomProjectId && (
        <Modal
          setModalOpen={(open: boolean) => {
            if (!open) setNomProjectId(null);
          }}
          title="Special Award Nominations"
        >
          <Nominations projectId={nomProjectId} />
        </Modal>
      )}
    </div>
  );
};

function uniqueJudges(rows: IJudgePortalAssignment[]) {
  const map = new Map<number, IJudgePortalAssignment>();
  rows
    .slice()
    .sort((a, b) => Number(b.captain) - Number(a.captain) || (a.judgeNumber ?? 0) - (b.judgeNumber ?? 0))
    .forEach((r) => {
      if (!map.has(r.personId)) map.set(r.personId, r);
    });
  return Array.from(map.values());
}

function uniqueProjects(rows: IJudgePortalAssignment[]) {
  const map = new Map<string, IJudgePortalAssignment>();
  rows
    .slice()
    .sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime())
    .forEach((r) => {
      const key = `${r.roundId}-${r.roomId}-${r.projectId}-${r.time}`;
      if (!map.has(key)) map.set(key, r);
    });
  return Array.from(map.values());
}

function projectJudgeRows(rows: IJudgePortalAssignment[], project: IJudgePortalAssignment) {
  return rows
    .filter(
      (r) =>
        r.projectId === project.projectId &&
        r.roundId === project.roundId &&
        r.roomId === project.roomId &&
        r.time === project.time
    )
    .sort((a, b) => (a.judgeNumber ?? 0) - (b.judgeNumber ?? 0));
}

const AssignmentTab = ({
  data,
  rows,
  onOpenRanking,
  onLock,
  onNominate,
}: {
  data: IJudgePortalData;
  rows: IJudgePortalAssignment[];
  onOpenRanking: () => void;
  onLock: () => void;
  onNominate: (projectKey: string) => void;
}) => {
  const group = rows[0];
  let displayRows = rows;
  if (group.isFinalRound) {
    displayRows = rows.filter((r) => r.captain);
  }
  const isLocked = group.roomStatus === 'Locked';
  const readyToSubmitToNhd = group.roomStatus === 'RankingSubmitted';
  const userIsCaptain = displayRows.some((x) => x.personId === data.userPersonId && x.captain);
  const canManage = data.isStaff || userIsCaptain;
  const allFinished =
    data.isStaff ||
    uniqueJudges(displayRows).every(
      (j) => j.evaluationState === 'Finished' || j.evaluationState === 'No Show'
    );

  return (
    <div data-testid="judge-portal-assignment-tab">
      {group.isFinalRound && (
        <div className="alert alert-info">
          <strong>
            <i className="fa fa-exclamation-triangle" /> Finals Round.
          </strong>
        </div>
      )}
      {!supportedCategory(group.categoryName) && (
        <div className="alert alert-danger">
          <strong>
            <i className="fa fa-exclamation-triangle" /> Online Judging is not supported for this
            category, some features will not work.
          </strong>
        </div>
      )}

      <div className="row">
        <div className="col-sm-6">
          <div>
            Division: <strong>{group.divisionName}</strong>
          </div>
          <div>
            Category: <strong>{group.categoryName}</strong>
          </div>
          <div>
            Building: <strong>{group.roomBuilding}</strong>
          </div>
          <div>
            Room: <strong>{group.roomName}</strong>
          </div>
          <div style={{ marginTop: '0.5rem' }}>
            {!data.isStaff ? (
              <a
                target="_blank"
                rel="noreferrer"
                href={StoreX.BuildUrl(
                  `../JudgePortal/ViewNotes?user=${data.userId}&assignment=${group.judgeGroupAssignmentId}`
                )}
              >
                <i className="fa fa-file-text-o" /> View &amp; Print Notes
              </a>
            ) : (
              uniqueJudges(displayRows).map((j) => (
                <div key={j.personId}>
                  <a
                    target="_blank"
                    rel="noreferrer"
                    href={StoreX.BuildUrl(
                      `../JudgePortal/ViewNotes?user=${j.personGuidId}&assignment=${group.judgeGroupAssignmentId}`
                    )}
                  >
                    <i className="fa fa-file-text-o" /> View &amp; Print Notes - {j.firstName}{' '}
                    {j.lastName}
                  </a>
                </div>
              ))
            )}
          </div>
        </div>
        <div className="col-sm-6 text-right">
          {isLocked && (
            <div className="alert alert-warning text-left">
              <strong>
                <i className="fa fa-exclamation-triangle" /> Judging has been locked for this room.
              </strong>
            </div>
          )}
          {canManage && (
            <div>
              {(!isLocked || data.isStaff) && (
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={!allFinished && !data.isStaff}
                  onClick={onOpenRanking}
                >
                  <i className="fa fa-certificate" /> Submit Ranking
                </button>
              )}{' '}
              {readyToSubmitToNhd && (
                <button type="button" className="btn btn-primary" onClick={onLock}>
                  Lock Judging Room
                </button>
              )}
              {data.isStaff && (
                <div style={{ marginTop: '0.5rem' }}>
                  <a
                    target="_blank"
                    rel="noreferrer"
                    href={StoreX.BuildUrl(
                      `../JudgePortal/PrintRankingSheet?RoundId=${group.roundId}&roomId=${group.roomId}`
                    )}
                  >
                    <i className="fa fa-file-text-o" /> Print Ranking Sheet
                  </a>
                  <br />
                  {data.showEvalsInJudgePortal && (
                    <a
                      target="_blank"
                      rel="noreferrer"
                      href={StoreX.BuildUrl(
                        `../JudgePortal/PrintEval?RoundId=${group.roundId}&entryId=-1&roomId=${group.roomId}`
                      )}
                    >
                      <i className="fa fa-file-text-o" /> Print Eval Sheet(s)
                    </a>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {data.showEntriesInJudgePortal &&
        uniqueProjects(displayRows).map((project) => {
          const userIsJudge = displayRows.find(
            (x) => x.personId === data.userPersonId && x.projectId === project.projectId
          );
          const cateName = (project.categoryName || '').toLowerCase();
          const judges = projectJudgeRows(displayRows, project);

          return (
            <div
              key={`${project.projectId}-${project.time}`}
              className="soft-border clearfix assignment-group"
            >
              <div className="row">
                <div className="col-sm-4">
                  <strong>{project.projectDisplayId}</strong> {formatTime(project.time)}
                  <br />
                  {project.title}
                  {project.paperFileKey && (
                    <div>
                      {cateName.includes('exh') ? 'Exhibit: ' : 'Paper: '}
                      <strong>
                        <a href={paperUrl(project.paperFileKey)} target="_blank" rel="noreferrer">
                          View Paper
                        </a>
                      </strong>
                    </div>
                  )}
                  {project.entryUrl && (
                    <div>
                      Website:{' '}
                      <strong>
                        <a href={websiteUrl(project.entryUrl)} target="_blank" rel="noreferrer">
                          {websiteUrl(project.entryUrl)}
                        </a>
                      </strong>
                    </div>
                  )}
                  {project.entryVideoLink?.toLowerCase().startsWith('http') && (
                    <div>
                      {cateName.includes('documentary') && data.historyDayShowJudgesDocLink && (
                        <>
                          Documentary Link{' '}
                          <strong>
                            <a href={project.entryVideoLink} target="_blank" rel="noreferrer">
                              Click here to view.
                            </a>
                          </strong>
                        </>
                      )}
                      {cateName.includes('perf') && (
                        <>
                          Performance Link{' '}
                          <strong>
                            <a href={project.entryVideoLink} target="_blank" rel="noreferrer">
                              Click here to view.
                            </a>
                          </strong>
                        </>
                      )}
                      {cateName.includes('exh') && (
                        <>
                          Exhibit{' '}
                          <strong>
                            <a href={project.entryVideoLink} target="_blank" rel="noreferrer">
                              Click here to view.
                            </a>
                          </strong>
                        </>
                      )}
                    </div>
                  )}
                  {project.writtenMaterialsFileKey && (
                    <div>
                      Written Materials:{' '}
                      <strong>
                        <a
                          href={fileUrl(project.writtenMaterialsFileKey)}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Click to Open
                        </a>
                      </strong>
                    </div>
                  )}
                  {project.documentaryFileKey && data.historyDayShowJudgesDocLink && (
                    <div>
                      Documentary:{' '}
                      <strong>
                        <a
                          href={fileUrl(project.documentaryFileKey)}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Click to Open
                        </a>
                      </strong>
                    </div>
                  )}
                  {project.performanceFileKey && data.historyDayShowJudgesPerformanceLink && (
                    <div>
                      Performance:{' '}
                      <strong>
                        <a
                          href={fileUrl(project.performanceFileKey)}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Click to Open
                        </a>
                      </strong>
                    </div>
                  )}
                  {data.historyDayShowJudgesDocLink &&
                    project.documentaryLink?.toLowerCase().startsWith('http') && (
                      <div>
                        Documentary Link:{' '}
                        <strong>
                          <a href={project.documentaryLink} target="_blank" rel="noreferrer">
                            Click here to view.
                          </a>
                        </strong>
                      </div>
                    )}
                  {project.performanceLink?.toLowerCase().startsWith('http') && (
                    <div>
                      Performance Link:{' '}
                      <strong>
                        <a href={project.performanceLink} target="_blank" rel="noreferrer">
                          Click here to view.
                        </a>
                      </strong>
                    </div>
                  )}
                  {userIsJudge && !isLocked && data.showEvalsInJudgePortal && (
                    <>
                      <br />
                      <a
                        className="btn btn-primary btn-xs"
                        target="_blank"
                        rel="opener noreferrer"
                        href={entryHref(project, 'self')}
                      >
                        View &amp; Evaluate
                      </a>
                    </>
                  )}
                </div>
                <div className="col-sm-4">
                  {project.roundRank != null && project.roundRank > 0 && (
                    <span>
                      <i className="fa fa-certificate" /> {project.roundRank}
                    </span>
                  )}
                  {data.isStaff && data.showEvalsInJudgePortal && (
                    <>
                      <br />
                      <a
                        target="_blank"
                        rel="noreferrer"
                        href={StoreX.BuildUrl(
                          `../JudgePortal/PrintEval?RoundId=${project.roundId}&entryId=${project.projectId}&roomId=-1`
                        )}
                      >
                        <i className="fa fa-file-text-o" /> Print Eval Sheet(s)
                      </a>
                    </>
                  )}
                  {data.specialAwardsNominationEnabled && canManage && (
                    <>
                      <br />
                      <a className="click" onClick={() => onNominate(project.projectKey)}>
                        <i className="fa fa-trophy" /> Special Award Nominations
                      </a>
                    </>
                  )}
                </div>
                <div className="col-sm-4 text-right">
                  {data.showEvalsInJudgePortal && (
                    <table className="table table-striped" style={{ marginBottom: '0.4em' }}>
                      <tbody>
                        {judges.map((judge) => (
                          <tr key={`${judge.personId}-${judge.evaluationId}`}>
                            <td className="text-left">
                              {judge.firstName} {judge.lastName}
                            </td>
                            <td>
                              {judge.hasMajorViolation && (
                                <span className="text-danger" style={{ fontSize: 'larger' }}>
                                  <i className="fa fa-flag" />
                                </span>
                              )}
                              {!judge.hasMajorViolation && judge.hasViolation && (
                                <span className="text-warning" style={{ fontSize: 'larger' }}>
                                  <i className="fa fa-flag" />
                                </span>
                              )}
                            </td>
                            <td>
                              {data.isStaff ? (
                                <a
                                  target="_blank"
                                  rel="opener noreferrer"
                                  href={entryHref(judge, 'for', judge.personGuidId)}
                                >
                                  <i className="fa fa-gavel" /> View &amp; Evaluate
                                </a>
                              ) : (
                                judge.evaluationState === 'Finished' && (
                                  <a
                                    target="_blank"
                                    rel="opener noreferrer"
                                    href={entryHref(judge, 'review', judge.personGuidId)}
                                  >
                                    <i className="fa fa-eye" /> View
                                  </a>
                                )
                              )}
                            </td>
                            <td className="text-right">{formatState(judge.evaluationState)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          );
        })}
    </div>
  );
};

export default JudgePortalPage;
